import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { RdsProgressSpinnerComponent } from '@rds/angular-components';
import { switchMap } from 'rxjs';

import { RoomBookingRedirectType } from '@bookly/shared';

import { BookingRedirectService } from '../../services/booking-redirect.service';

@Component({
  selector: 'bus-booking-request-redirect',
  imports: [RdsProgressSpinnerComponent],
  templateUrl: './booking-request-redirect.component.html',
  styleUrl: './booking-request-redirect.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingRequestRedirectComponent implements OnInit {
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #bookingRedirectService = inject(BookingRedirectService);

  protected readonly RoomBookingRedirectType = RoomBookingRedirectType;
  protected readonly bookingRequestRedirect =
    this.#bookingRedirectService.bookingRequestRedirect;
  protected readonly error = this.#bookingRedirectService.error;
  protected readonly isLoading =
    this.#bookingRedirectService.isRedirectDataLoading;

  ngOnInit(): void {
    this.#activatedRoute.params
      .pipe(
        switchMap(({ bookingId }) =>
          this.#bookingRedirectService.determineBookingRequestRedirect(
            bookingId
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#navigateToView());
  }

  #navigateToView() {
    const redirectData = this.bookingRequestRedirect();
    if (redirectData) {
      const type = redirectData.roomBookingRedirectType;
      if (type === RoomBookingRedirectType.ROOM_HOST_PANEL) {
        void this.#router.navigate(
          [
            'room-host',
            'booking-request',
            redirectData.roomDexId,
            redirectData.bookingId,
          ],
          { queryParamsHandling: 'merge' }
        );
      }
      if (type === RoomBookingRedirectType.SERVICE_PROVIDER_PANEL) {
        void this.#router.navigate(
          [
            'service-provider',
            'booking-request',
            redirectData.roomDexId,
            redirectData.bookingId,
          ],
          { queryParamsHandling: 'merge' }
        );
      }
      if (type === RoomBookingRedirectType.MY_BOOKING_DETAILS) {
        void this.#router.navigate([
          'dashboard',
          'bookings',
          redirectData.bookingId,
          redirectData.roomDexId,
        ]);
      }
    }
  }
}
